import { useState, useEffect, useContext } from "react";
import { StyleSheet, ScrollView, Text, View, Pressable, Platform } from "react-native";

import { StatusBar } from "expo-status-bar";
import * as SplashScreen from "expo-splash-screen";

import { KeyboardProvider, KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { SafeAreaProvider, SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import AsyncStorage from "@react-native-async-storage/async-storage";

import CalculationsContextProvider, { CalculationsContext } from "./store/calculationsCtx";
import { useWebLayout } from "./hooks/useWebLayout";

import { appColors } from "./constants/colors";

import CreateCalculatorModal from "./components/UI/CalculatorModal";
import AppInstallBanner from "./components/AppInstallPrompt";

import InputsContainer from "./components/InputsContainer";
import ResultsContainer from "./components/ResultsContainer";
import AdvancedSettings from "./components/AdvancedSettings";
import GPTTable from "./components/GPTTable";
import PieChart from "./components/PieChart";
import MixedChart from "./components/MixedChart";
import Footer from "./components/Footer";

SplashScreen.preventAutoHideAsync();

// Inject global web styles — navy backdrop + hidden scrollbars

if (Platform.OS === "web") {
  const style = document.createElement("style");
  style.textContent = `
    html, body { margin: 0; overflow-y: scroll; }
    #root { min-height: 100vh; display: flex; justify-content: center; }
    * { scrollbar-width: none; -ms-overflow-style: none; }
    *::-webkit-scrollbar { display: none; }
  `;
  document.head.appendChild(style);
}

function Root() {
  const [appIsReady, setAppIsReady] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const { columnStyle } = useWebLayout();
  const insets = useSafeAreaInsets();

  const calculationsCtx = useContext(CalculationsContext);
  const calculators = calculationsCtx.calculators;
  const selectedCalculator = calculators.find(e => e.active);

  useEffect(() => {
    const init = async () => {
      try {
        const savedCalculators = await AsyncStorage.getItem("calculators");

        if (savedCalculators && JSON.parse(savedCalculators).length !== 0) {
          const parsed = JSON.parse(savedCalculators);
          calculationsCtx.setCalculators(parsed);
          calculationsCtx.setInputs(parsed.find(e => e.active).inputs);
          return;
        }

        setModalVisible(true);
      } catch {
      } finally {
        setAppIsReady(true);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (appIsReady) SplashScreen.hideAsync();
  }, [appIsReady]);

  const handleCreate = async newCalc => {
    const calc = {
      ...newCalc,
      active: true,
      inputs: {
        loanAmount: newCalc.loanAmount,
        options: 0,
        deposit: 0,
        tradeInValue: 0,
        term: newCalc.term,
        rate: newCalc.rate,
        balloon: 0,
        initFee: 0,
        serviceFee: 0,
        extraPayments: [],
        rateChanges: [],
      },
    };

    try {
      const saved = await AsyncStorage.getItem("calculators");

      if (saved && JSON.parse(saved).length !== 0) {
        const existing = JSON.parse(saved);
        const merged = [...existing, calc].map(item => ({
          ...item,
          active: item.hasOwnProperty("name") ? item.name === calc.name : item.active,
        }));

        await AsyncStorage.setItem("calculators", JSON.stringify(merged));
        calculationsCtx.setCalculators(merged);
        calculationsCtx.setInputs(calc.inputs);
        return;
      }

      await AsyncStorage.setItem("calculators", JSON.stringify([calc]));
      calculationsCtx.setCalculators([calc]);
      calculationsCtx.setInputs(calc.inputs);
    } catch {}
  };

  const handleSelectedCalculator = async activeCal => {
    try {
      const newArray = calculators.map(item => ({
        ...item,
        active: item.hasOwnProperty("name") ? item.name === activeCal : item.active,
      }));

      await AsyncStorage.setItem("calculators", JSON.stringify(newArray));
      calculationsCtx.setCalculators(newArray);
      calculationsCtx.setInputs(newArray.find(e => e.active).inputs);
    } catch {}
  };

  const handleRemoveCalculator = async () => {
    const newArray = calculators.filter(obj => obj.name !== selectedCalculator.name);

    if (newArray.length === 0) {
      setModalVisible(true);
    } else {
      calculationsCtx.setCalculators(prev => {
        const updated = prev.filter(obj => obj.name !== selectedCalculator.name);
        updated[0].active = true;
        calculationsCtx.setInputs(updated[0].inputs);
        return updated;
      });
    }

    await AsyncStorage.setItem("calculators", JSON.stringify(newArray));
  };

  if (!appIsReady) return null;

  const instalment = calculationsCtx.results?.instalment || "—";
  const loanType = selectedCalculator?.loanType || "";
  const currencyCode = selectedCalculator?.currency?.code || "";
  const term = calculationsCtx.inputs?.term || "";

  const ScrollContainer = Platform.OS === "web" ? View : KeyboardAwareScrollView;
  const scrollProps =
    Platform.OS === "web"
      ? { style: [styles.scroll, columnStyle] }
      : { style: [styles.scroll, columnStyle], showsVerticalScrollIndicator: false };

  return (
    <View style={{ flex: 1 }}>
      {Platform.OS !== "web" && <View style={[styles.statusBarBg, { height: insets.top }]} />}
      <ScrollContainer {...scrollProps}>
        {/* ── Navy header ── */}
        <StatusBar style="light" />
        <SafeAreaView edges={["top", "left", "right"]} style={styles.header}>
          <View style={styles.headerTop}>
            <Text style={styles.appTitle}>Instalment Calculator</Text>
            <Pressable
              style={({ pressed }) => [styles.addBtn, pressed && styles.addBtnPressed]}
              onPress={() => setModalVisible(true)}
            >
              <Text style={styles.addBtnText}>+</Text>
            </Pressable>
          </View>

          {/* Calculator tabs */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tabsScroll}
            contentContainerStyle={styles.tabsContent}
          >
            {calculators.map(calculator => {
              const isActive = selectedCalculator?.name === calculator.name;
              return (
                <Pressable
                  key={calculator.name}
                  style={[styles.tab, isActive && styles.tabActive]}
                  onPress={() => handleSelectedCalculator(calculator.name)}
                >
                  {isActive && <View style={styles.tabDot} />}
                  <Text style={[styles.tabText, isActive && styles.tabTextActive]}>{calculator.name}</Text>
                </Pressable>
              );
            })}
          </ScrollView>

          {/* Hero instalment */}
          <View style={styles.hero}>
            <Text style={styles.heroLabel}>MONTHLY INSTALMENT</Text>
            <Text style={styles.heroAmount}>{instalment}</Text>
            {loanType ? (
              <Text style={styles.heroSub}>
                {loanType}
                {currencyCode ? ` · ${currencyCode}` : ""}
                {term ? ` · ${term} months` : ""}
              </Text>
            ) : null}
          </View>
        </SafeAreaView>

        {/* ── Body ── */}
        <View style={styles.body}>
          <CreateCalculatorModal
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            onCreate={handleCreate}
            calculators={calculators.length}
          />

          <ResultsContainer />

          <InputsContainer
            category={selectedCalculator?.loanType}
            currency={selectedCalculator?.currency}
            selectedCalculator={selectedCalculator}
            calculators={calculators}
          />

          <AdvancedSettings />

          <GPTTable currency={selectedCalculator?.currency} />

          <PieChart />

          <MixedChart />

          <Pressable
            style={({ pressed }) => [styles.deleteBtn, pressed && styles.deleteBtnPressed]}
            onPress={handleRemoveCalculator}
          >
            <Text style={styles.deleteBtnText}>Delete calculator</Text>
          </Pressable>

          {/* <Footer /> */}
          <SafeAreaView edges={["bottom", "left", "right"]}>
            <Footer />
            <AppInstallBanner />
          </SafeAreaView>
        </View>
      </ScrollContainer>
    </View>
  );
}

export default function App() {
  return (
    <KeyboardProvider>
      <SafeAreaProvider>
        <CalculationsContextProvider>
          <Root />
        </CalculationsContextProvider>
      </SafeAreaProvider>
    </KeyboardProvider>
  );
}

const styles = StyleSheet.create({
  scroll: {
    // flex: 1,
    flex: Platform.OS === "web" ? undefined : 1,
    backgroundColor: appColors.pageBg,
  },

  // ── Header ──
  header: {
    backgroundColor: appColors.primary,
    paddingHorizontal: 16,
    paddingTop: 6,
    paddingBottom: 24,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
  },

  headerTop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },

  appTitle: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "500",
    letterSpacing: 0.2,
  },

  addBtn: {
    backgroundColor: "#fff",
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
  },

  addBtnPressed: {
    backgroundColor: appColors.primaryTint,
  },

  addBtnText: {
    color: appColors.primary,
    fontSize: 22,
    lineHeight: 26,
    fontWeight: "400",
  },

  // Tabs
  tabsScroll: {
    flexGrow: 0,
  },

  tabsContent: {
    gap: 7,
    paddingRight: 4,
  },

  tab: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.35)",
    paddingVertical: 6,
    paddingHorizontal: 14,
  },

  tabActive: {
    backgroundColor: "#fff",
    borderColor: "#fff",
  },

  tabDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: appColors.accent,
  },

  tabText: {
    color: "rgba(255,255,255,0.8)",
    fontSize: 11,
    fontWeight: "400",
  },

  tabTextActive: {
    color: appColors.primary,
    fontWeight: "500",
  },

  // Hero
  hero: {
    alignItems: "center",
    marginTop: 18,
  },

  heroLabel: {
    color: appColors.primaryMuted,
    fontSize: 10,
    letterSpacing: 0.8,
    marginBottom: 4,
  },

  heroAmount: {
    color: "#fff",
    fontSize: 40,
    fontWeight: "500",
    letterSpacing: -1.5,
    lineHeight: 44,
  },

  heroSub: {
    color: appColors.primaryLight,
    fontSize: 11,
    marginTop: 6,
  },

  // ── Body ──
  body: {
    paddingTop: 14,
    paddingBottom: 28,
    alignItems: "center",
    gap: 11,
  },

  // Delete
  deleteBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: appColors.dangerLight,
    borderWidth: 0.5,
    borderColor: appColors.dangerBorder,
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },

  deleteBtnPressed: {
    backgroundColor: "#ffe0de",
  },

  deleteBtnText: {
    color: appColors.danger,
    fontSize: 12,
    fontWeight: "500",
  },

  statusBarBg: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: appColors.primary,
    zIndex: 10,
  },
});
